// rv-product-recommendations: loaded only where the section is used (sections/rv-product-recommendations.liquid)

class RvProductRecommendations extends HTMLElement {
  async connectedCallback() {
    if (!this.hasAttribute('data-loaded')) {
      const fallback = this.querySelector('template[data-rv-recs-fallback]');
      const loaded =
        (await this.load(this.dataset.url)) ||
        (await this.load(this.dataset.fallbackUrl)) ||
        this.useFallback(fallback);
      if (!loaded) return this.hideSection();
    }
    this.setup();
    document.dispatchEvent(new CustomEvent('rv:recommendations-loaded'));
  }

  async load(url) {
    if (!url) return false;
    try {
      const html = await fetch(url).then((response) => response.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('rv-product-recommendations');
      if (!fresh || !fresh.querySelector('.rv-recs__slide')) return false;
      this.innerHTML = fresh.innerHTML;
      return true;
    } catch (error) {
      return false;
    }
  }

  useFallback(template) {
    if (!template || !template.content.querySelector('.rv-recs__slide')) return false;
    this.replaceChildren(template.content.cloneNode(true));
    return true;
  }

  setup() {
    this.setAttribute('data-has-products', '');
    this.track = this.querySelector('.rv-recs__track');
    if (!this.track) return this.hideSection();

    this.querySelectorAll('.rv-recs__arrow').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        this.track.scrollBy({ left: this.track.clientWidth * Number(arrow.dataset.direction), behavior: 'smooth' });
      });
    });
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    new ResizeObserver(() => this.updateArrows()).observe(this.track);

    this.dedupe();
    document.addEventListener('rv:recommendations-loaded', () => this.dedupe());
  }

  // Remove products already shown by a higher-priority carousel (Complete the look), then keep the maximum.
  dedupe() {
    const priority = Number(this.dataset.priority);
    const shownElsewhere = new Set(
      [...document.querySelectorAll('rv-product-recommendations')]
        .filter((other) => other !== this && Number(other.dataset.priority) < priority)
        .flatMap((other) => [...other.querySelectorAll('.rv-recs__slide:not([hidden])')].map((slide) => slide.dataset.productId))
    );
    this.querySelectorAll('.rv-recs__slide').forEach((slide) => {
      if (shownElsewhere.has(slide.dataset.productId)) slide.remove();
    });
    // Keep spare products hidden (not removed) so they can fill in if more duplicates appear later.
    const max = Number(this.dataset.max || 8);
    const slides = [...this.querySelectorAll('.rv-recs__slide')];
    slides.forEach((slide, index) => (slide.hidden = index >= max));
    if (!slides.length) return this.hideSection();
    this.updateArrows();
  }

  updateArrows() {
    const arrows = this.querySelector('[data-rv-recs-arrows]');
    if (!arrows || !this.track) return;
    const [prev, next] = arrows.querySelectorAll('.rv-recs__arrow');
    const max = this.track.scrollWidth - this.track.clientWidth;
    arrows.toggleAttribute('data-hidden', max <= 1);
    prev.disabled = this.track.scrollLeft <= 1;
    next.disabled = this.track.scrollLeft >= max - 1;
  }

  hideSection() {
    this.innerHTML = '';
    this.removeAttribute('data-has-products');
    const wrapper = this.closest('.shopify-section');
    if (wrapper && !window.Shopify?.designMode) wrapper.hidden = true;
  }
}

if (!customElements.get('rv-product-recommendations')) {
  customElements.define('rv-product-recommendations', RvProductRecommendations);
}
